import { Card, theme, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DetalheDaPessoa } from '@titularis/api-client';

/**
 * O ritmo da conta nos últimos trinta dias, em respostas por dia.
 *
 * **Serve a uma pergunta que nenhum total responde: é constância ou foi uma
 * tarde?** Vinte respostas podem ser vinte dias de dois ou uma sentada de um
 * domingo, e as duas contas precisam de conversas diferentes - a primeira está
 * dentro do hábito, a segunda experimentou e sumiu.
 *
 * A série vem do mesmo `getPerformance` que o app mostra ao aluno, com os dias
 * parados incluídos: sem eles as barras ficariam lado a lado e um mês com três
 * dias de estudo se pareceria com um mês inteiro.
 *
 * **Some quando não há resposta nenhuma.** Um gráfico de trinta zeros ocupa meia
 * tela para dizer o que a linha "0 respostas" já disse lá em cima.
 */
export function RitmoDaPessoa({ ritmo }: { ritmo: DetalheDaPessoa['estudo']['ritmo'] }) {
  const { token } = theme.useToken();

  const total = ritmo.reduce((soma, dia) => soma + dia.total, 0);
  if (total === 0) return null;

  const dados = ritmo.map((dia) => ({
    ...dia,
    // Erros como a diferença: empilhados, a altura da barra é o dia inteiro.
    erros: dia.total - dia.acertos,
    rotulo: new Date(`${dia.dia}T12:00:00`).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    }),
  }));

  return (
    <Card size="small" title="Ritmo dos últimos 30 dias">
      {/*
        A moldura com `overflow: hidden` e `minWidth: 0` **é o que impede a
        página de ganhar rolagem horizontal**, e não enfeite.

        O `ResponsiveContainer` mede o próprio pai e escreve a largura em pixels
        no `<svg>`. Dentro de uma coluna que se dimensiona pelo conteúdo, isso
        vira um laço que só cresce: o svg empurra o pai, o pai remede maior, o
        svg cresce de novo. Medido em 18/09/2026 na ficha da pessoa - 1347px de
        conteúdo numa janela de 1193px. Com o transbordo recortado, o pai deixa
        de crescer e a medição estabiliza na largura da coluna.
      */}
      <div style={{ width: '100%', minWidth: 0, height: 180, overflow: 'hidden' }}>
        <ResponsiveContainer width="100%" height="100%">
        <BarChart data={dados} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={token.colorSplit} vertical={false} />
          <XAxis
            dataKey="rotulo"
            tick={{ fill: token.colorTextSecondary, fontSize: 11 }}
            stroke={token.colorSplit}
            // Um rótulo a cada cinco dias: trinta datas coladas viram uma tarja.
            interval={4}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: token.colorTextSecondary, fontSize: 11 }}
            stroke={token.colorSplit}
          />
          <Tooltip
            cursor={{ fill: token.colorFillSecondary }}
            contentStyle={{
              background: token.colorBgElevated,
              border: `1px solid ${token.colorSplit}`,
              borderRadius: token.borderRadius,
              color: token.colorText,
            }}
            labelStyle={{ color: token.colorText }}
          />
          <Bar
            dataKey="acertos"
            name="Acertos"
            stackId="dia"
            fill={token.colorSuccess}
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="erros"
            name="Erros"
            stackId="dia"
            fill={token.colorErrorBorder}
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
        </ResponsiveContainer>
      </div>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Acertos embaixo, erros em cima: a altura da barra é o dia inteiro.
      </Typography.Text>
    </Card>
  );
}
